import React from 'react';
import styled from '@emotion/styled';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const keyToName = {
  amount_no_cod: `Оплата по счету`,
  amount_online: `Предоплата`,
  amount_cod: `Оплата при доставке`,
};

const declOfNum = (number, words) =>
  words[
    number % 100 > 4 && number % 100 < 20
      ? 2
      : [2, 0, 1, 1, 1, 2][number % 10 < 5 ? number % 10 : 5]
  ];

const Wrapper = styled(ResponsiveContainer)`
  .recharts-wrapper .recharts-cartesian-grid-vertical line:nth-last-of-type(-n + 2) {
    stroke-opacity: 0;
  }

  .recharts-tooltip-item {
    padding: 0 !important;
    margin-top: 2px;
  }

  .custom-tooltip {
    border: none;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
    border-radius: 0.25rem;
    opacity: 0.95;
    font-weight: 500;
    backdrop-filter: blur(2px);
    text-align: left;
    padding: 10px;
    background-color: white;

    h5 {
      margin: 0px 0px 10px;
      font-weight: 600;
      text-align: left;
    }

    p {
      padding: 0 !important;
      margin-top: 2px;
      margin-bottom: 0;
    }
  }
`;

const CustomTooltip = ({ active, payload, label, keyToLabel }) => {
  if (active) {
    return (
      <div className="custom-tooltip">
        <h5>
          {keyToLabel[label]} | {payload[0].payload.amount}{' '}
          {declOfNum(payload[0].payload.amount, [`заказ`, `заказа`, `заказов`])}
        </h5>
        {payload.map((item, index, array) => (
          <p key={item.name} style={{ color: item.color === `#e3eaef` ? `#98a6bd` : item.color }}>
            {keyToName[item.name]}:{' '}
            {(() => {
              switch (index) {
                case 0:
                  return item.value - payload[1].value;
                case 1:
                  return item.value - payload[2].value;
                case 2:
                  return item.value;
                default:
                  return item.value;
              }
            })()}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

const OrdersQuantityChart = (props) => {
  const { title, weeklyValue, monthlyValue, allTimeValue, data, keyToLabel, formatNumber } = props;

  const dotStyle = {
    stroke: `#999999`,
    strokeWidth: 1,
    strokeDasharray: 0,
    fill: `#ffffff`,
    r: 3,
  };

  const { t } = useAutoTranslation();

  const formattedData = data.map((item) => ({
    ...item,
    amount_online: item.amount_online,
    amount_cod: item.amount_online + item.amount_cod,
    amount_no_cod: item.amount_online + item.amount_cod + item.amount_no_cod,
  }));

  return (
    <div className="col-xl-4 col-md-12">
      <div className="card">
        <div className="card-body">
          <h4 className="header-title mb-0">{title}</h4>
          <div id="cardCollpase2" className="collapse pt-3 show">
            <div className="text-center">
              <div className="row mt-2">
                <div className="col-4">
                  <h4>{formatNumber(weeklyValue)}</h4>
                  <p className="text-muted font-13 mb-0 text-truncate">{t('За неделю')}</p>
                </div>
                <div className="col-4">
                  <h4>{formatNumber(monthlyValue)}</h4>
                  <p className="text-muted font-13 mb-0 text-truncate">{t('За месяц')}</p>
                </div>
                <div className="col-4">
                  <h4>{formatNumber(allTimeValue)}</h4>
                  <p className="text-muted font-13 mb-0 text-truncate">{t('За всё время')}</p>
                </div>
              </div>

              <div className="mt-3">
                <Wrapper width="100%" height={300}>
                  <AreaChart
                    data={formattedData}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="point"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: `#98a6ad` }}
                    />
                    <YAxis
                      width={30}
                      axisLine={false}
                      tickLine={false}
                      tickCount={5}
                      tick={{ fill: `#98a6ad` }}
                      domain={[() => 0, (dataMax) => Math.round(dataMax * 1.35)]}
                    />
                    <CartesianGrid
                      vertical={false}
                      stroke="#f5f5f5"
                      strokeWidth={1}
                      active={false}
                    />
                    <Tooltip
                      cursor={false}
                      content={CustomTooltip}
                      labelStyle={{
                        fontWeight: 600,
                        marginBottom: 5,
                        textAlign: `left`,
                      }}
                      keyToLabel={keyToLabel}
                    />
                    <Area
                      type="linear"
                      dataKey="amount_no_cod"
                      stroke="#e3eaef"
                      fillOpacity={1}
                      fill="#f9fafb"
                      dot={dotStyle}
                      activeDot={false}
                    />
                    <Area
                      type="linear"
                      dataKey="amount_cod"
                      stroke="#7e57c2"
                      fillOpacity={1}
                      fill="#a894cb"
                      dot={dotStyle}
                      activeDot={false}
                    />
                    <Area
                      type="linear"
                      dataKey="amount_online"
                      stroke="#4fc6e1"
                      fillOpacity={1}
                      fill="#9cdfee"
                      dot={dotStyle}
                      activeDot={false}
                    />
                  </AreaChart>
                </Wrapper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersQuantityChart;
